import { CoveredPartyPayloadType } from '@clearcover/core-app-types';
import React, { FC, useContext } from 'react';
import { DriverReportTable } from './DriverReportTable';
import styles from './styles.module.css';
import { getIn, useFormikContext } from 'formik';
import { FormInput } from 'Shared/components/FormInput/FormInput';
import { PolicyContext } from 'Policies/utils';
import { STATE_OPTIONS } from 'Shared/constants/states';
import {
  getCoverageErrorMessage,
  getCoverageValues,
  handleCoverageChange,
  handleDateChange,
} from 'QuoteApp/components/ReportDetails/PriorPolicyChallengeForm/PriorPolicyChallengeForm';
import { Button, Modal } from '@clearcover/atlas';
import { ModalPromptContent } from 'Shared/components';

export const DriverPriorPolicies: FC = () => {
  const {
    driverOptions: {
      // @ts-expect-error - priorPolicy is not defined in the driverOptions type
      priorPolicy: { insuranceCarrierOptions },
    },
  } = useContext(PolicyContext);
  const { handleBlur, setFieldValue, values, errors, handleChange } =
    useFormikContext<CoveredPartyPayloadType>();

  const carrierOptionsMap =
    insuranceCarrierOptions?.reduce(
      (
        acc: Record<string, string>,
        option: { label: string; value: string }
      ) => {
        acc[option.value] = option.label;
        return acc;
      },
      {}
    ) ?? {};

  const markAsEdited = (index: number) => {
    if (values.reports.priorPolicies[index]?.challenge !== 'edited') {
      setFieldValue(`reports.priorPolicies.${index}.challenge`, 'edited');
      setFieldValue(`reports.priorPolicies.${index}.source`, 'User Reported');
    }
  };

  const handleAddRecord = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setFieldValue('reports.priorPolicies', [
      ...values.reports.priorPolicies,
      {
        source: 'User Reported',
        currentCarrierName: '',
        policyNumber: '',
        startDate: '',
        endDate: '',
        amBestNumber: '',
        state: '',
        coverages: {
          bodilyInjury: '',
          combinedSingleLimit: '',
          personalInjuryProtection: '',
        },
        pniPolicy: true,
      },
    ]);
  };

  const handleRemoveRecord = (index: number) => {
    setFieldValue(`reports.priorPolicies.${index}.challenge`, 'removed');
  };

  const renderSource = (source: string, challenge: string | undefined) => {
    return source === 'User Reported' && challenge === 'edited' ? 'AD' : source;
  };

  return (
    <>
      <div className={styles.sectionHeaderPriorPolicies}>
        <h3>Prior Insurance</h3>
        <Button
          variant="secondary"
          size="sm"
          className={styles.addRecordButton}
          onClick={handleAddRecord}
        >
          + Add Record
        </Button>
      </div>
      <DriverReportTable
        autoColumnAlignment
        data-testid="prior-policies-report-table"
        headers={[
          'SOURCE',
          'CARRIER',
          'POLICY NUMBER',
          'EFFECTIVE DATE',
          'END DATE',
          'AM BEST NUMBER',
          'STATE',
          'BI LIMIT',
          'CSL LIMIT',
          'PIP LIMIT',
          '',
        ]}
        tableRows={
          values.reports.priorPolicies
            ?.map(
              (
                priorPolicy: CoveredPartyPayloadType['reports']['priorPolicies'][number],
                index: number
              ) => {
                // Skip removed records
                if (priorPolicy.challenge === 'removed') {
                  return null;
                }
                return {
                  data: [
                    <div
                      key={index}
                      className={styles.priorPoliciesData}
                      title={priorPolicy.source}
                    >
                      {renderSource(priorPolicy.source, priorPolicy.challenge)}
                    </div>,
                    <div key={index} title={priorPolicy.currentCarrierName}>
                      <FormInput
                        variant="combobox"
                        name={`reports.priorPolicies.${index}.currentCarrierName`}
                        onChange={(value: string) => {
                          setFieldValue(
                            `reports.priorPolicies.${index}.currentCarrierName`,
                            carrierOptionsMap[value]
                          );
                          markAsEdited(index);
                        }}
                        onBlur={handleBlur}
                        value={
                          values.reports.priorPolicies[index].currentCarrierName
                        }
                        options={carrierOptionsMap}
                        required
                        errorMessage={getIn(
                          errors,
                          `reports.priorPolicies.${index}.currentCarrierName`
                        )}
                      />
                    </div>,
                    <div key={index} title={priorPolicy.policyNumber}>
                      <FormInput
                        variant="text"
                        name={`reports.priorPolicies.${index}.policyNumber`}
                        value={values.reports.priorPolicies[index].policyNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue(
                            `reports.priorPolicies.${index}.policyNumber`,
                            e.target.value
                          );
                          markAsEdited(index);
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        required
                        errorMessage={getIn(
                          errors,
                          `reports.priorPolicies.${index}.policyNumber`
                        )}
                      />
                    </div>,
                    <div key={index} title={priorPolicy.startDate}>
                      <FormInput
                        className={styles.formInputDate}
                        variant="text"
                        placeholder="MM/DD/YYYY"
                        value={values.reports.priorPolicies[index].startDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleDateChange(
                            `reports.priorPolicies.${index}.startDate`,
                            setFieldValue,
                            true
                          )(e);
                          markAsEdited(index);
                        }}
                        required
                        onBlur={handleBlur}
                        errorMessage={getIn(
                          errors,
                          `reports.priorPolicies.${index}.startDate`
                        )}
                      />
                    </div>,
                    <div key={index} title={priorPolicy.endDate}>
                      <FormInput
                        className={styles.formInputDate}
                        variant="text"
                        placeholder="MM/DD/YYYY"
                        value={values.reports.priorPolicies[index].endDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleDateChange(
                            `reports.priorPolicies.${index}.endDate`,
                            setFieldValue,
                            true
                          )(e);
                          handleChange(e);
                          markAsEdited(index);
                        }}
                        onBlur={handleBlur}
                        required
                        errorMessage={getIn(
                          errors,
                          `reports.priorPolicies.${index}.endDate`
                        )}
                      />
                    </div>,
                    <div key={index} title={priorPolicy.amBestNumber}>
                      <FormInput
                        variant="text"
                        name={`reports.priorPolicies.${index}.amBestNumber`}
                        value={values.reports.priorPolicies[index].amBestNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue(
                            `reports.priorPolicies.${index}.amBestNumber`,
                            e.target.value
                          );
                          handleChange(e);
                          markAsEdited(index);
                        }}
                        maxLength={10}
                      />
                    </div>,
                    <div key={index} title={priorPolicy.state}>
                      <FormInput
                        variant="select"
                        name={`reports.priorPolicies.${index}.state`}
                        value={
                          values.reports.priorPolicies[index].state.includes(
                            '-'
                          )
                            ? values.reports.priorPolicies[index].state.split(
                                '-'
                              )[1]
                            : values.reports.priorPolicies[index].state
                        }
                        onChange={(value: string) => {
                          setFieldValue(
                            `reports.priorPolicies.${index}.state`,
                            value
                          );
                          markAsEdited(index);
                        }}
                        options={STATE_OPTIONS}
                        required
                        errorMessage={getIn(
                          errors,
                          `reports.priorPolicies.${index}.state`
                        )}
                      />
                    </div>,
                    <div key={index} title={priorPolicy.coverages.bodilyInjury}>
                      <FormInput
                        name={`reports.priorPolicies.${index}.coverages.bodilyInjury`}
                        variant="text"
                        placeholder="100K/300K"
                        value={
                          values.reports.priorPolicies[
                            index
                          ].coverages.bodilyInjury?.toString() || ''
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleCoverageChange(
                            `reports.priorPolicies.${index}.coverages.bodilyInjury`,
                            'BI',
                            setFieldValue
                          )(e);
                          markAsEdited(index);
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        errorMessage={getCoverageErrorMessage(
                          values.reports.priorPolicies[index].coverages
                            .bodilyInjury,
                          'BI',
                          getCoverageValues(values, `reports`, true, index),
                          errors,
                          `reports.priorPolicies.${index}`,
                          true
                        )}
                      />
                    </div>,
                    <div
                      key={index}
                      title={priorPolicy.coverages.combinedSingleLimit}
                    >
                      <FormInput
                        name={`reports.priorPolicies.${index}.coverages.combinedSingleLimit`}
                        variant="text"
                        placeholder="100K"
                        value={
                          values.reports.priorPolicies[
                            index
                          ].coverages.combinedSingleLimit?.toString() || ''
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleCoverageChange(
                            `reports.priorPolicies.${index}.coverages.combinedSingleLimit`,
                            'CSL',
                            setFieldValue
                          )(e);
                          handleChange(e);
                          markAsEdited(index);
                        }}
                        onBlur={handleBlur}
                        errorMessage={getCoverageErrorMessage(
                          values.reports.priorPolicies[index].coverages
                            .combinedSingleLimit,
                          'CSL',
                          getCoverageValues(values, `reports`, true, index),
                          errors,
                          `reports.priorPolicies.${index}`,
                          true
                        )}
                      />
                    </div>,
                    <div
                      key={index}
                      title={priorPolicy.coverages.personalInjuryProtection}
                    >
                      <FormInput
                        name={`reports.priorPolicies.${index}.coverages.personalInjuryProtection`}
                        variant="text"
                        placeholder="10K"
                        value={
                          values.reports.priorPolicies[
                            index
                          ].coverages.personalInjuryProtection?.toString() || ''
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleCoverageChange(
                            `reports.priorPolicies.${index}.coverages.personalInjuryProtection`,
                            'PIP',
                            setFieldValue
                          )(e);
                          markAsEdited(index);
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        errorMessage={getCoverageErrorMessage(
                          values.reports.priorPolicies[index].coverages
                            .personalInjuryProtection,
                          'PIP',
                          getCoverageValues(values, `reports`, true, index),
                          errors,
                          `reports.priorPolicies.${index}`,
                          true
                        )}
                      />
                    </div>,
                    <div key={index} className={styles.removeRecordButton}>
                      <Modal
                        toggle={(openModal) => (
                          <Button
                            variant="danger-secondary"
                            onClick={(
                              e: React.MouseEvent<HTMLButtonElement>
                            ) => {
                              e.preventDefault();
                              openModal();
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      >
                        {(close) => (
                          <ModalPromptContent
                            cancelButtonText="Cancel"
                            confirmButtonText="Remove"
                            confirmButtonVariant="danger-secondary"
                            onCancel={close}
                            onConfirm={() => {
                              close();
                              handleRemoveRecord(index);
                            }}
                          >
                            <h3>Remove Prior Policy</h3>
                            <p>
                              Are you sure you want to remove this prior policy?
                            </p>
                          </ModalPromptContent>
                        )}
                      </Modal>
                    </div>,
                  ],
                };
              }
            )
            .filter(Boolean) || []
        }
      />
    </>
  );
};
